import { h } from '@omni-torn/ui';
import { bestFit, COMPANY_HISTORY_TERMS, COMPANY_SHARE_TERMS, companyDayOf, companyHeatmap, companyMoney, companyRecap, dayMoney, earnedDay, mergeDays, positionFits, pricingAdvice, readCompany, staffTrends, starForecast, workingStatsEffectiveness, type Company, type CompanyDay, type CompanyDirectoryAnswer, type CompanyShareAnswer, type CompanySnapshot, type CompanyType, type CompanyTypesAnswer, type Competitor, type DayMoney, type Employee, type Position } from '@omni-torn/shared';
import { server, session, torn, TornError } from '../session';
import { csvButton } from '../log-kit/ui';
import { ago, box, ext, facts, kids, lineChart, loading, money, num, problem, profileUrl, short, signed, table, tabs } from '../ui';

/**
 * The Company page (TornStats' company pages, ours): your own company read with your key when you open it — the
 * director's view when you direct it, else its profile — with its history (one day a Torn day, kept on the server while
 * your history is on: this page and background tracking add it); every employee's best position (Torn's effectiveness
 * formula, packages/shared company.ts); an efficiency calculator for any working stats; and the directory of every
 * company of a type (public, asked by the server with its own key and cached).
 *
 * The Daily tracker (website roadmap S18 — Torn Intel's company tracker and advisor; packages/shared company-report.ts):
 * profit and expenses, the week's recap, the income heatmap, the weekly star forecast, pricing advice and each
 * employee's effectiveness over time — from your own days, and (terms 9, COMPANY_SHARE_TERMS) the company's days kept for
 * its Omni-Torn members (GET /api/company/shared: the server's daily read with each member's own key; the staff list only
 * for the director).
 */

type Tab = 'mine' | 'tracker' | 'calc' | 'directory';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'mine', label: 'Your company' },
  { id: 'tracker', label: 'Daily tracker' },
  { id: 'calc', label: 'Efficiency calculator' },
  { id: 'directory', label: 'Directory' },
];

/** Torn said this key isn't its director's (this tab): the profile only from then on. */
let notDirector = false;
let typesAsk: Promise<CompanyTypesAnswer> | null = null;
const types = (): Promise<CompanyTypesAnswer> => {
  typesAsk ??= server<CompanyTypesAnswer>('GET', '/api/company/types').catch((err) => {
    typesAsk = null;
    throw err;
  });
  return typesAsk;
};
const corpUrl = (id: number) => `https://www.torn.com/joblist.php#/p=corpinfo&ID=${id}`;
const stars = (n: number | null) => (n === null ? '—' : `${n} ★`);

export async function renderCompany(view: HTMLElement): Promise<void> {
  const body = h('div', {});
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Company'), h('p', { class: 'muted' }, 'Your company, the best position for each employee, and every company of a type.')), body);
  let tab: Tab = (TABS.find((t) => `#${t.id}` === location.hash)?.id ?? 'mine') as Tab;
  const content = h('div', { 'data-company': 'content' });
  let mine: Promise<{ company: Company; director: boolean } | { problem: string }> | null = null;
  const own = () => (mine ??= readMine());
  const draw = async () => {
    content.replaceChildren(loading());
    const shown = tab;
    const nodes = tab === 'mine' ? await mineTab(own) : tab === 'tracker' ? await trackerTab(own) : tab === 'calc' ? await calcTab(own) : await directoryTab(own);
    if (shown === tab && content.isConnected) content.replaceChildren(...nodes);
  };
  body.replaceChildren(
    tabs(TABS, tab, (id) => {
      tab = id as Tab;
      history.replaceState(null, '', `/company#${id}`);
      void draw();
    }),
    content,
  );
  await draw();
}

/** Your company with your key: the director's view first (unless Torn said no before), else the profile. */
async function readMine(): Promise<{ company: Company; director: boolean } | { problem: string }> {
  let director = !notDirector;
  let body: unknown;
  try {
    body = await torn('/company/', { selections: director ? 'profile,detailed,employees,stock' : 'profile' }, 60_000);
  } catch (err) {
    if (!(err instanceof TornError) || !director || (err.code !== 7 && err.code !== 16)) return { problem: err instanceof TornError && (err.code === 6 || err.code === 7) ? 'Torn says you aren’t in a company.' : (err as Error).message };
    notDirector = true;
    director = false;
    try {
      body = await torn('/company/', { selections: 'profile' }, 60_000);
    } catch (e) {
      return { problem: e instanceof TornError && (e.code === 6 || e.code === 7) ? 'Torn says you aren’t in a company.' : (e as Error).message };
    }
  }
  const company = readCompany(body);
  if (!company) return { problem: 'Torn didn’t send a company — are you in one?' };
  // Today's figures for your history (kept only with your history on).
  // Only from terms version 8 (COMPANY_HISTORY_TERMS) — below it nothing leaves this page.
  const prefs = session.me()?.prefs;
  if (prefs?.history !== false && (prefs?.terms ?? 0) >= COMPANY_HISTORY_TERMS) await server('POST', '/api/me/company', { day: companyDayOf(company, Date.now(), director) }).catch(() => null);
  return { company, director };
}

// ---------------------------------------------------------------- your company

async function mineTab(own: () => ReturnType<typeof readMine>): Promise<Node[]> {
  const r = await own();
  if ('problem' in r) return [h('p', { class: 'notice' }, r.problem)];
  const { company: c, director } = r;
  const [typeList, hist] = await Promise.all([types().catch(() => null), session.me()?.prefs.history !== false ? server<{ history: CompanyDay[] }>('GET', '/api/me/company?days=400').catch(() => ({ history: [] })) : Promise.resolve(null)]);
  const type = typeList?.types.find((t) => t.id === c.type) ?? null;
  const out: Node[] = [];
  out.push(
    box(
      c.name,
      [
        facts([
          ['Type', type?.name ?? (c.type ? `Type ${c.type}` : '—')],
          ['Rating', stars(c.rating)],
          ['Employees', `${num(c.employeesHired)} of ${num(c.employeesCapacity)}`],
          ['Income', `${money(c.dailyIncome)} yesterday · ${money(c.weeklyIncome)} this week`],
          ['Customers', `${num(c.dailyCustomers)} yesterday · ${num(c.weeklyCustomers ?? null)} this week`],
          ['Age', c.daysOld !== null ? `${num(c.daysOld)} days` : '—'],
          ...(director
            ? ([
                ['Funds', money(c.funds)],
                ['Popularity', c.popularity !== null ? `${c.popularity}%` : '—'],
                ['Efficiency', c.efficiency !== null ? `${c.efficiency}%` : '—'],
                ['Environment', c.environment !== null ? `${c.environment}%` : '—'],
                ['Trains', num(c.trainsAvailable)],
                ['Advertising', `${money(c.advertising)} a day`],
                ['Value', money(c.value)],
              ] as Array<[string, string]>)
            : []),
        ]),
        director ? null : h('p', { class: 'muted small' }, 'You see what every employee sees; its director also sees funds, popularity, working stats and stock here.'),
      ],
      { extra: ext(corpUrl(c.id), 'On Torn ↗'), id: 'company-profile' },
    ),
  );
  out.push(employeesBox(c, director, type));
  if (director && c.stock.length) {
    out.push(
      box('Stock', [
        table(
          c.stock,
          [
            { label: 'Product', get: (p) => p.name, sort: (p) => p.name },
            { label: 'Price', get: (p) => money(p.price), sort: (p) => p.price, cls: 'r' },
            { label: 'Cost', get: (p) => money(p.cost), sort: (p) => p.cost, cls: 'r' },
            { label: 'RRP', get: (p) => money(p.rrp), sort: (p) => p.rrp, cls: 'r' },
            { label: 'In stock', get: (p) => num(p.inStock), sort: (p) => p.inStock, cls: 'r' },
            { label: 'On order', get: (p) => num(p.onOrder), sort: (p) => p.onOrder, cls: 'r' },
            { label: 'Sold yesterday', get: (p) => num(p.soldAmount), sort: (p) => p.soldAmount, cls: 'r' },
            { label: 'Worth', get: (p) => money(p.soldWorth), sort: (p) => p.soldWorth, cls: 'r' },
            { label: 'Days left', get: (p) => (p.soldAmount > 0 ? num((p.inStock + p.onOrder) / p.soldAmount, 1) : '—'), sort: (p) => (p.soldAmount > 0 ? (p.inStock + p.onOrder) / p.soldAmount : 1e9), cls: 'r' },
          ],
          { initial: 6, remember: 'company-stock' },
        ),
      ]),
    );
  }
  out.push(historyBox(hist?.history ?? null, c));
  return out;
}

function employeesBox(c: Company, director: boolean, type: CompanyType | null): HTMLElement {
  const positions = type?.positions ?? [];
  type Row = Employee & { fit: ReturnType<typeof bestFit>; now: Position | undefined };
  const rows: Row[] = c.employees.map((e) => ({ ...e, fit: director && e.manual !== null ? bestFit(e, positions) : null, now: positions.find((p) => p.name === e.position) }));
  const nowEff = (r: Row) => (r.now && r.manual !== null ? workingStatsEffectiveness(r, r.now) : null);
  const cols: Parameters<typeof table<Row>>[1] = [
    { label: 'Employee', get: (r) => ext(profileUrl(r.id), r.name), sort: (r) => r.name.toLowerCase() },
    { label: 'Position', get: (r) => r.position, sort: (r) => r.position },
    { label: 'Days', get: (r) => num(r.daysInCompany), sort: (r) => r.daysInCompany ?? -1, cls: 'r' },
  ];
  if (director) {
    cols.push(
      { label: 'Wage', get: (r) => money(r.wage), sort: (r) => r.wage ?? -1, cls: 'r' },
      { label: 'MAN', get: (r) => short(r.manual), sort: (r) => r.manual ?? -1, cls: 'r' },
      { label: 'INT', get: (r) => short(r.intelligence), sort: (r) => r.intelligence ?? -1, cls: 'r' },
      { label: 'END', get: (r) => short(r.endurance), sort: (r) => r.endurance ?? -1, cls: 'r' },
      { label: 'Effectiveness', get: (r) => h('span', { title: r.addiction ? `Addiction ${r.addiction}` : '' }, num(r.effectiveness), r.addiction ? h('span', { class: 'st-red small' }, ` (${r.addiction})`) : null), sort: (r) => r.effectiveness ?? -1, cls: 'r' },
      {
        label: 'Best position',
        get: (r) => {
          if (!r.fit) return positions.length ? '—' : h('span', { class: 'muted' }, 'positions not loaded');
          const here = nowEff(r);
          const same = r.fit.position.name === r.position && r.fit.meets;
          return h('span', { 'data-best': r.fit.position.name }, same ? h('span', { class: 't-green' }, `✓ ${r.fit.position.name}`) : r.fit.position.name, h('span', { class: 'muted small' }, ` · ${r.fit.effectiveness}${!same && here !== null ? ` (now ${here})` : ''}${r.fit.meets ? '' : ' · below its needs'}`));
        },
        sort: (r) => r.fit?.effectiveness ?? -1,
      },
    );
  }
  cols.push({ label: 'Seen', get: (r) => (r.lastAction ? ago(r.lastAction * 1000) : '—'), sort: (r) => -(r.lastAction ?? 0) });
  return box(`Employees (${rows.length})`, [
    director ? h('p', { class: 'muted small' }, 'Best position: the most demanding one their working stats meet (Torn’s working-stats effectiveness there, out of the formula on Torn’s wiki); “now” is what they get where they are.') : null,
    table(rows, cols, { initial: 0, desc: false, remember: 'company-employees' }),
  ]);
}

const SERIES: Array<{ id: string; label: string; get: (d: CompanyDay) => number | null; fmt?: (n: number) => string }> = [
  { id: 'income', label: 'Daily income', get: (d) => d.dailyIncome, fmt: (n) => `$${short(n)}` },
  { id: 'customers', label: 'Customers', get: (d) => d.dailyCustomers },
  { id: 'rating', label: 'Rating', get: (d) => d.rating },
  { id: 'popularity', label: 'Popularity', get: (d) => d.popularity },
  { id: 'efficiency', label: 'Efficiency', get: (d) => d.efficiency },
  { id: 'funds', label: 'Funds', get: (d) => d.funds, fmt: (n) => `$${short(n)}` },
];

function historyBox(days: CompanyDay[] | null, c: Company): HTMLElement {
  if (!days) return box('History', h('p', { class: 'muted' }, 'Your history is off (Account → Keep a history), so nothing is kept day to day.'));
  const list = days.filter((d) => d.companyId === c.id);
  const series = SERIES.filter((s) => list.some((d) => s.get(d) !== null));
  let pick = series[0]?.id ?? 'income';
  const chart = h('div', {});
  const drawChart = () => {
    const s = SERIES.find((x) => x.id === pick)!;
    chart.replaceChildren(lineChart(list.flatMap((d) => (s.get(d) !== null ? [{ at: Date.parse(`${d.day}T12:00:00Z`), v: s.get(d)! }] : [])), { label: s.label, ...(s.fmt ? { format: s.fmt } : {}) }));
  };
  drawChart();
  return box(
    'History',
    kids(
      h('p', { class: 'muted small' }, `${list.length} day${list.length === 1 ? '' : 's'} kept — one a Torn day, from this page and background tracking. Only you see them.`),
      series.length > 1
        ? tabs(
            series.map((s) => ({ id: s.id, label: s.label })),
            pick,
            (id) => {
              pick = id;
              drawChart();
            },
          )
        : null,
      chart,
      list.length
        ? table(
            [...list].reverse().slice(0, 30),
            [
              { label: 'Day', get: (d) => d.day, sort: (d) => d.day },
              { label: 'Income', get: (d) => money(d.dailyIncome), sort: (d) => d.dailyIncome ?? -1, cls: 'r' },
              { label: 'Customers', get: (d) => num(d.dailyCustomers), sort: (d) => d.dailyCustomers ?? -1, cls: 'r' },
              { label: 'Rating', get: (d) => stars(d.rating), sort: (d) => d.rating ?? -1, cls: 'r' },
              { label: 'Employees', get: (d) => num(d.employees), sort: (d) => d.employees ?? -1, cls: 'r' },
              { label: 'Popularity', get: (d) => (d.popularity !== null ? `${d.popularity}%` : '—'), sort: (d) => d.popularity ?? -1, cls: 'r' },
            ],
            { initial: 0, remember: 'company-days' },
          )
        : null,
    ),
    { id: 'company-history' },
  );
}

// ---------------------------------------------------------------- the daily tracker

/** Your days (your own history) and the company's (kept for its Omni-Torn members, terms 9), one a Torn day. */
async function trackerDays(c: Company): Promise<{ days: CompanySnapshot[]; shared: CompanyShareAnswer | null; own: number }> {
  const historyOn = session.me()?.prefs.history !== false;
  const [own, shared] = await Promise.all([
    historyOn ? server<{ history: CompanyDay[] }>('GET', '/api/me/company?days=400').then((r) => r.history).catch(() => [] as CompanyDay[]) : Promise.resolve([] as CompanyDay[]),
    server<CompanyShareAnswer>('GET', '/api/company/shared?days=400').catch(() => null),
  ]);
  const mineDays: CompanySnapshot[] = own.filter((d) => d.companyId === c.id).map((d) => ({ ...d, wages: null, stock: [], staff: [] }));
  const theirs = shared?.member?.companyId === c.id ? shared.days : [];
  return { days: mergeDays(mineDays, theirs), shared, own: mineDays.length };
}

async function trackerTab(own: () => ReturnType<typeof readMine>): Promise<Node[]> {
  const r = await own();
  if ('problem' in r) return [h('p', { class: 'notice' }, r.problem)];
  const { company: c, director } = r;
  const { days, shared, own: ownCount } = await trackerDays(c);
  const out: Node[] = [];
  out.push(shareBox(shared, days.length, ownCount));
  out.push(moneyBox(c, director, days));
  out.push(recapBox(days));
  out.push(heatmapBox(days));
  out.push(forecastBox(c, days));
  if (director && c.stock.length) out.push(pricingBox(c, days));
  const trends = staffTrends(days);
  if (director && trends.length) out.push(staffBox(trends));
  return out;
}

function shareBox(a: CompanyShareAnswer | null, n: number, ownCount: number): HTMLElement {
  const lines: Node[] = [];
  lines.push(h('p', {}, `${n} day${n === 1 ? '' : 's'} kept${ownCount ? ` (${ownCount} from your own history)` : ''}.`));
  if (!a) lines.push(h('p', { class: 'muted small' }, 'The company’s shared days couldn’t be asked for just now.'));
  else if (!a.agreed) lines.push(h('p', { class: 'muted small', 'data-share': 'terms' }, `Sharing your company’s days with its other Omni-Torn members (and seeing theirs) comes with Omni-Torn’s terms version ${COMPANY_SHARE_TERMS}. Until you agree, only your own history is used here.`));
  else if (a.member) lines.push(h('p', { class: 'muted small', 'data-share': 'on' }, `Shared with your company: the server reads it once a day with each member’s own key (${a.members} member${a.members === 1 ? '' : 's'} using Omni-Torn — never who). ${a.member.director ? 'As its director you also see the staff list.' : 'Its director’s figures show when the director uses Omni-Torn; the staff list stays with the director.'}`));
  else lines.push(h('p', { class: 'muted small', 'data-share': 'none' }, a.note ?? 'No company found for you yet.'));
  return box('Your company’s days', lines, { id: 'company-share' });
}

const moneyFacts = (m: DayMoney): Array<[string, string]> => [
  ['Income', money(m.income)],
  ['Wages', money(m.wages)],
  ['Advertising', money(m.advertising)],
  ['Goods sold (at cost)', money(m.goods)],
  ['Expenses', money(m.expenses)],
  ['Profit', m.profit === null ? '—' : signed(m.profit, (x) => money(x))],
];

const MONEY_SERIES: Array<{ id: string; label: string; get: (d: CompanySnapshot) => number | null; fmt?: (n: number) => string }> = [
  { id: 'income', label: 'Income', get: (d) => d.dailyIncome, fmt: (n) => `$${short(n)}` },
  { id: 'expenses', label: 'Expenses', get: (d) => dayMoney(d).expenses, fmt: (n) => `$${short(n)}` },
  { id: 'profit', label: 'Profit', get: (d) => dayMoney(d).profit, fmt: (n) => `$${short(n)}` },
  { id: 'customers', label: 'Customers', get: (d) => d.dailyCustomers },
];

function moneyBox(c: Company, director: boolean, days: CompanySnapshot[]): HTMLElement {
  const today = director ? companyMoney(c, true) : (days.filter((d) => d.director).map(dayMoney).at(-1) ?? companyMoney(c, false));
  const series = MONEY_SERIES.filter((s) => days.filter((d) => s.get(d) !== null).length >= 2);
  let pick = series[0]?.id ?? 'income';
  const chart = h('div', {});
  const draw = () => {
    const s = MONEY_SERIES.find((x) => x.id === pick)!;
    chart.replaceChildren(lineChart(days.flatMap((d) => (s.get(d) !== null ? [{ at: Date.parse(`${earnedDay(d.day)}T12:00:00Z`), v: s.get(d)! }] : [])), { label: s.label, ...(s.fmt ? { format: s.fmt } : {}) }));
  };
  if (series.length) draw();
  const csv = csvButton(
    () => `company-${c.id}-days.csv`,
    () => ({
      header: ['day earned', 'income', 'customers', 'wages', 'advertising', 'goods', 'expenses', 'profit', 'rating', 'popularity', 'efficiency', 'environment', 'employees', 'funds'],
      rows: days.map((d) => {
        const m = dayMoney(d);
        return [earnedDay(d.day), m.income, d.dailyCustomers, m.wages, m.advertising, m.goods, m.expenses, m.profit, d.rating, d.popularity, d.efficiency, d.environment, d.employees, d.funds];
      }),
    }),
  );
  return box(
    'Profit & expenses',
    kids(
      h('p', { class: 'muted small' }, director ? 'Yesterday, from the director’s view now: Torn’s daily income less wages, the advertising budget and what the products sold cost.' : today.profit !== null ? 'From the director’s last read kept for the company.' : 'Wages, advertising and stock are in the director’s view only — they show here when the director uses Omni-Torn and agreed to share the company’s days.'),
      h('div', { 'data-money': 'today' }, facts(moneyFacts(today))),
      series.length > 1 ? tabs(series.map((s) => ({ id: s.id, label: s.label })), pick, (id) => ((pick = id), draw())) : null,
      series.length ? chart : h('p', { class: 'muted small' }, 'A chart shows once two days are kept.'),
      days.length ? h('div', { class: 'calc-row' }, csv) : null,
    ),
    { id: 'company-money' },
  );
}

function recapBox(days: CompanySnapshot[]): HTMLElement {
  const r = companyRecap(days);
  return box(
    'This week',
    kids(
      h('p', { class: 'muted small' }, 'The last 7 days kept against the 7 before them.'),
      h('ul', { class: 'company-recap', 'data-recap': '' }, ...r.lines.map((l) => h('li', {}, l))),
      r.now.days
        ? facts([
            ['Days kept', `${r.now.days} (before: ${r.before.days})`],
            ['Income', `${money(r.now.income)} (before: ${money(r.before.income)})`],
            ['Customers', `${num(r.now.customers)} (before: ${num(r.before.customers)})`],
            ...(r.now.profit !== null ? ([['Profit', `${money(r.now.profit)} (before: ${money(r.before.profit)})`]] as Array<[string, string]>) : []),
          ])
        : null,
    ),
    { id: 'company-recap' },
  );
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function heatmapBox(days: CompanySnapshot[]): HTMLElement {
  let metric: 'income' | 'customers' = 'income';
  const holder = h('div', {});
  const draw = () => {
    const m = companyHeatmap(days, metric === 'income' ? (d) => d.dailyIncome : (d) => d.dailyCustomers);
    if (!m.weeks.length) return holder.replaceChildren(h('p', { class: 'muted small' }, 'Nothing kept yet.'));
    const fmt = (v: number) => (metric === 'income' ? `$${short(v)}` : short(v));
    const level = (v: number | null) => (v === null || m.max === m.min ? (v === null ? -1 : 2) : Math.min(4, Math.floor(((v - m.min) / (m.max - m.min)) * 5)));
    const best = m.weekdays.reduce<number>((b, v, i) => (v !== null && (m.weekdays[b] === null || v > (m.weekdays[b] ?? -Infinity)) ? i : b), 0);
    holder.replaceChildren(
      ...kids(h(
        'div',
        { class: 'heatmap', role: 'table', 'aria-label': `${metric === 'income' ? 'Income' : 'Customers'} by day`, 'data-heatmap': '' },
        h('div', { class: 'heat-row heat-head', role: 'row' }, h('span', { class: 'heat-week', role: 'columnheader' }, 'Week of'), ...WEEKDAYS.map((d) => h('span', { role: 'columnheader' }, d))),
        ...m.weeks.map((w) =>
          h(
            'div',
            { class: 'heat-row', role: 'row' },
            h('span', { class: 'heat-week', role: 'rowheader' }, w.monday.slice(5)),
            ...w.cells.map((c) => h('span', { class: `heat-cell heat-${level(c.v)}`, role: 'cell', title: `${c.day}: ${c.v === null ? 'nothing kept' : fmt(c.v)}` }, c.v === null ? '' : fmt(c.v))),
          ),
        ),
        h('div', { class: 'heat-row heat-avg', role: 'row' }, h('span', { class: 'heat-week', role: 'rowheader' }, 'Average'), ...m.weekdays.map((v) => h('span', { role: 'cell' }, v === null ? '—' : fmt(v)))),
      ),
      m.weekdays[best] !== null ? h('p', { class: 'muted small' }, `Best day on average: ${WEEKDAYS[best]}.`) : null),
    );
  };
  draw();
  return box(
    'Heatmap',
    [
      h('p', { class: 'muted small' }, 'Each day the money was made (Torn’s daily figures are yesterday’s), darker for more.'),
      tabs(
        [
          { id: 'income', label: 'Income' },
          { id: 'customers', label: 'Customers' },
        ],
        metric,
        (id) => ((metric = id as typeof metric), draw()),
      ),
      holder,
    ],
    { id: 'company-heatmap' },
  );
}

function forecastBox(c: Company, days: CompanySnapshot[]): HTMLElement {
  const holder = h('div', { 'data-forecast': '' }, loading('Comparing with every company of your type…'));
  void (async () => {
    if (!c.type) return holder.replaceChildren(h('p', { class: 'muted small' }, 'Torn didn’t say your company’s type.'));
    try {
      const dir = await server<CompanyDirectoryAnswer>('GET', `/api/company/directory?type=${c.type}`);
      const f = starForecast({ id: c.id, rating: c.rating, weeklyIncome: c.weeklyIncome }, dir.list, days);
      holder.replaceChildren(
        ...kids(h('p', { 'data-verdict': f.verdict }, h('strong', {}, f.verdict === 'up' ? '▲ Likely up. ' : f.verdict === 'down' ? '▼ Could go down. ' : f.verdict === 'hold' ? '■ Likely the same. ' : ''), f.line),
        f.byRating.length
          ? table(
              f.byRating,
              [
                { label: 'Stars', get: (g) => h('span', { class: g.rating === c.rating ? 'is-mine' : '' }, stars(g.rating)), sort: (g) => g.rating },
                { label: 'Companies', get: (g) => num(g.companies), sort: (g) => g.companies, cls: 'r' },
                { label: 'Middle weekly income', get: (g) => money(g.median), sort: (g) => g.median, cls: 'r' },
              ],
              { initial: 0, desc: true, remember: 'company-forecast' },
            )
          : null,
        f.changes.length ? h('p', { class: 'muted small' }, `Your stars changed: ${f.changes.slice(-4).map((x) => `${x.day} (${x.from}→${x.to})`).join(', ')}.`) : null),
      );
    } catch (err) {
      holder.replaceChildren(problem(err));
    }
  })();
  return box('Weekly star forecast', [h('p', { class: 'muted small' }, 'Torn re-rates companies once a week and doesn’t publish how. A rule of thumb, not Torn’s formula: your week’s income against the middle of the companies of your type a star above and a star below (the public directory).'), holder], { id: 'company-forecast' });
}

function pricingBox(c: Company, days: CompanySnapshot[]): HTMLElement {
  const advice = pricingAdvice(c.stock, days);
  return box(
    'Pricing advice',
    [
      h('p', { class: 'muted small' }, 'Rules of thumb from your stock now and the days kept — not Torn’s formula. “Prices kept” shows each price a product sold at, how many a day and the profit a day it made.'),
      table(
        advice,
        [
          { label: 'Product', get: (a) => a.name, sort: (a) => a.name },
          { label: 'Price', get: (a) => money(a.price), sort: (a) => a.price, cls: 'r' },
          { label: 'Margin', get: (a) => h('span', { class: a.margin < 0 ? 't-red' : '' }, money(a.margin)), sort: (a) => a.margin, cls: 'r' },
          { label: 'Of RRP', get: (a) => (a.ofRrp === null ? '—' : `${a.ofRrp}%`), sort: (a) => a.ofRrp ?? 0, cls: 'r' },
          { label: 'Days left', get: (a) => num(a.daysLeft, 1), sort: (a) => a.daysLeft ?? 1e9, cls: 'r' },
          { label: 'Prices kept', get: (a) => (a.seen.length ? a.seen.map((s) => `${money(s.price)}: ${num(s.soldPerDay)}/day, ${money(s.profitPerDay)}/day (${s.days}d)`).join(' · ') : '—') },
          { label: 'Advice', get: (a) => h('span', { 'data-advice': a.name }, a.tips.join(' ')) },
        ],
        { initial: 0, desc: false, remember: 'company-pricing' },
      ),
    ],
    { id: 'company-pricing' },
  );
}

function staffBox(trends: ReturnType<typeof staffTrends>): HTMLElement {
  return box(
    'Employees over time',
    [
      h('p', { class: 'muted small' }, 'Each employee’s effectiveness now against a week before, from the staff lists kept (only you, as its director, see these).'),
      table(
        trends,
        [
          { label: 'Employee', get: (t) => ext(profileUrl(t.id), t.name), sort: (t) => t.name.toLowerCase() },
          { label: 'Now', get: (t) => num(t.now), sort: (t) => t.now ?? -1, cls: 'r' },
          { label: 'A week before', get: (t) => num(t.then), sort: (t) => t.then ?? -1, cls: 'r' },
          { label: 'Change', get: (t) => h('span', { class: (t.change ?? 0) < 0 ? 't-red' : (t.change ?? 0) > 0 ? 't-green' : '' }, signed(t.change)), sort: (t) => t.change ?? 0, cls: 'r' },
          { label: 'Days kept', get: (t) => num(t.days), sort: (t) => t.days, cls: 'r' },
        ],
        { initial: 3, desc: false, remember: 'company-staff' },
      ),
    ],
    { id: 'company-staff' },
  );
}

// ---------------------------------------------------------------- the calculator

async function calcTab(own: () => ReturnType<typeof readMine>): Promise<Node[]> {
  let list: CompanyType[];
  try {
    list = (await types()).types.filter((t) => t.positions.some((p) => p.manRequired + p.intRequired + p.endRequired > 0));
  } catch (err) {
    return [problem(err)];
  }
  const r = await own().catch(() => null);
  const ownType = r && 'company' in r ? r.company.type : null;
  const pick = h('select', { 'aria-label': 'Company type', 'data-calc': 'type' }, ...list.map((t) => h('option', { value: String(t.id), ...(t.id === ownType ? { selected: '' } : {}) }, t.name)));
  const input = (label: string, stat: string) => h('label', { class: 'calc-field' }, label, h('input', { type: 'number', min: '0', step: '1', inputmode: 'numeric', 'data-stat': stat, value: '0' }));
  const fields = h('div', { class: 'calc-fields' }, input('Manual labor', 'manual'), input('Intelligence', 'intelligence'), input('Endurance', 'endurance'));
  const status = h('span', { class: 'save-status', role: 'status' });
  const mineBtn = h('button', { type: 'button' }, 'Use my working stats');
  const result = h('div', { 'data-calc': 'result' });
  const val = (stat: string) => Math.max(0, Number((fields.querySelector(`[data-stat=${stat}]`) as HTMLInputElement).value) || 0);
  const draw = () => {
    const t = list.find((x) => x.id === Number(pick.value));
    if (!t) return result.replaceChildren(h('p', { class: 'muted' }, 'No company types.'));
    const stats = { manual: val('manual'), intelligence: val('intelligence'), endurance: val('endurance') };
    const fits = positionFits(stats, t.positions);
    const best = fits[0];
    result.replaceChildren(
      best ? h('p', { 'data-calc': 'best' }, 'Best position: ', h('strong', {}, best.position.name), ` — working-stats effectiveness ${best.effectiveness}${best.meets ? '' : ' (below its needs)'}.`) : h('p', { class: 'muted' }, 'This type has no positions with needs.'),
      table(
        fits,
        [
          { label: 'Position', get: (f) => h('span', {}, f.position.name, f.position.special && f.position.special !== 'None' ? h('span', { class: 'muted small' }, ` · ${f.position.special}`) : null), sort: (f) => f.position.name },
          { label: 'Needs MAN / INT / END', get: (f) => `${short(f.position.manRequired)} / ${short(f.position.intRequired)} / ${short(f.position.endRequired)}`, sort: (f) => f.position.manRequired + f.position.intRequired + f.position.endRequired },
          { label: 'Effectiveness', get: (f) => num(f.effectiveness), sort: (f) => f.effectiveness, cls: 'r' },
          { label: 'Meets it', get: (f) => (f.meets ? h('span', { class: 't-green' }, '✓') : h('span', { class: 'muted small' }, ['manual', 'intelligence', 'endurance'].filter((k) => f.short[k as keyof typeof f.short] > 0).map((k) => `${short(f.short[k as keyof typeof f.short])} ${k === 'manual' ? 'MAN' : k === 'intelligence' ? 'INT' : 'END'}`).join(' · ') + ' short')), sort: (f) => (f.meets ? 1 : 0), cls: 'c' },
          { label: 'Trains', get: (f) => [f.position.manGain ? `MAN +${f.position.manGain}` : '', f.position.intGain ? `INT +${f.position.intGain}` : '', f.position.endGain ? `END +${f.position.endGain}` : ''].filter(Boolean).join(' · ') || '—' },
        ],
        { remember: 'company-calc' },
      ),
    );
  };
  pick.addEventListener('change', draw);
  fields.addEventListener('input', draw);
  mineBtn.addEventListener('click', async () => {
    mineBtn.disabled = true;
    status.className = 'save-status';
    status.textContent = 'Asking Torn…';
    try {
      const b = (await torn('/user/', { selections: 'workstats' })) as Record<string, unknown>;
      for (const [stat, keyName] of [['manual', 'manual_labor'], ['intelligence', 'intelligence'], ['endurance', 'endurance']] as const) (fields.querySelector(`[data-stat=${stat}]`) as HTMLInputElement).value = String(Number(b[keyName]) || 0);
      status.textContent = '';
      draw();
    } catch (err) {
      status.className = 'save-status bad';
      status.textContent = (err as Error).message;
    }
    mineBtn.disabled = false;
  });
  draw();
  return [
    box('Efficiency calculator', [
      h('p', { class: 'muted' }, 'Torn’s working-stats effectiveness (its wiki’s formula): 45 for each stat a position asks for when you have exactly what it needs, less below, a little more above (5 for each doubling). Merits, the director’s education, management and addiction add or take on top.'),
      h('div', { class: 'calc-row' }, pick, mineBtn, status),
      fields,
      result,
    ]),
  ];
}

// ---------------------------------------------------------------- the directory

async function directoryTab(own: () => ReturnType<typeof readMine>): Promise<Node[]> {
  let list: CompanyType[];
  try {
    list = (await types()).types;
  } catch (err) {
    return [problem(err)];
  }
  const r = await own().catch(() => null);
  const mineC = r && 'company' in r ? r.company : null;
  const pick = h('select', { 'aria-label': 'Company type', 'data-dir': 'type' }, ...list.map((t) => h('option', { value: String(t.id), ...(t.id === mineC?.type ? { selected: '' } : {}) }, t.name)));
  const holder = h('div', { 'data-dir': 'list' });
  const draw = async () => {
    holder.replaceChildren(loading('Asking for every company of this type…'));
    try {
      const a = await server<CompanyDirectoryAnswer & { stale?: boolean }>('GET', `/api/company/directory?type=${Number(pick.value)}`);
      holder.replaceChildren(
        h('p', { class: 'muted small' }, `${a.list.length} companies · read ${ago(a.at)}${a.stale ? ' (Torn didn’t answer just now: the last list)' : ''}.`),
        table<Competitor>(
          a.list,
          [
            { label: 'Company', get: (x) => h('span', { class: x.id === mineC?.id ? 'is-mine' : '' }, ext(corpUrl(x.id), x.name), x.id === mineC?.id ? h('span', { class: 'muted small' }, ' (yours)') : null), sort: (x) => x.name.toLowerCase() },
            { label: 'Rating', get: (x) => stars(x.rating), sort: (x) => x.rating ?? -1, cls: 'r' },
            { label: 'Staff', get: (x) => `${num(x.employeesHired)}/${num(x.employeesCapacity)}`, sort: (x) => x.employeesHired ?? -1, cls: 'r' },
            { label: 'Daily income', get: (x) => money(x.dailyIncome), sort: (x) => x.dailyIncome ?? -1, cls: 'r' },
            { label: 'Customers', get: (x) => num(x.dailyCustomers), sort: (x) => x.dailyCustomers ?? -1, cls: 'r' },
            { label: 'Weekly income', get: (x) => money(x.weeklyIncome), sort: (x) => x.weeklyIncome ?? -1, cls: 'r' },
            { label: 'Age', get: (x) => (x.daysOld !== null ? `${num(x.daysOld)}d` : '—'), sort: (x) => x.daysOld ?? -1, cls: 'r' },
          ],
          { initial: 5, remember: 'company-directory' },
        ),
      );
    } catch (err) {
      holder.replaceChildren(problem(err));
    }
  };
  pick.addEventListener('change', () => void draw());
  await draw();
  return [box('Every company of a type', [h('p', { class: 'muted' }, 'Public figures from Torn, the same for everyone; the list is asked again at most once an hour.'), h('div', { class: 'calc-row' }, pick), holder])];
}
